import type { MutableRefObject } from "react";
import { api } from "../../services/api";
import { ShowGenericErrorToast, ShowToast } from "../../services/toast";
import posthog from "posthog-js";
import { getLoggerRef } from "../../integrations/logger/logger-bridge";
import type { NoiseReductionType } from "./noiseReduction";

// A failure here means a conversational turn was likely dropped, so the generic message
// tells the user to repeat themselves.
const MISSED_TURN_ERROR_LEAD =
  "Something went wrong and we may have missed what you said. Please repeat it.";

export const isInterrupted = (data: any) => {
  const reason = data?.response?.status_details?.reason;
  return data?.response?.status === "cancelled" && (reason === "turn_detected" || reason === "client_cancelled");
};

// Streaming/incremental events fire many times per response — logging every one floods
// Datadog now that production's log level is INFO (was WARN).
export const isHighFrequencyRealtimeEvent = (eventType: string | undefined) =>
  !!eventType && eventType.endsWith(".delta");

export interface RealtimeMessageHandlerDeps {
  dataChannel: MutableRefObject<any>;
  contextRequestGenerationRef: MutableRefObject<number>;
  isWaitingForModelResponseRef: MutableRefObject<boolean>;
  isUserCurrentlySpeakingRef: MutableRefObject<boolean>;
  pendingAssistantTranscriptRef: MutableRefObject<string>;
  lastCompletedAssistantMessageRef: MutableRefObject<string>;
  baseInstructionsRef: MutableRefObject<string>;
  noiseReductionTypeRef: MutableRefObject<NoiseReductionType>;
  transcriptRef: MutableRefObject<{ role: string; content: string }[]>;
  sentenceCacheRef: MutableRefObject<string[]>;
  timeLeftRef: MutableRefObject<number>;
  isCountdownCompleteRef: MutableRefObject<boolean>;
  setIsUploading: (value: boolean) => void;
  setIsConnected: (value: boolean) => void;
  // Snapshotted at creation time (init() only runs once per call), matching the
  // original inline handler's closure semantics — not refs.
  callDetails: any;
}

// Tunable — flicker gaps in production logs were as tight as ~6ms; needs real-call QA
// to dial in further.
const VAD_RAPID_FIRE_GRACE_PERIOD_MS = 500;

type ContextFetchResult = { ok: true; context: any } | { ok: false };

/**
 * Builds the handler for `dc.addEventListener("message", ...)` — every OpenAI
 * Realtime server event for one call session. Created once, inside init(),
 * when the data channel is created.
 *
 * Root cause of the VAD rapid-fire issue this guards against: semantic_vad can report
 * a turn complete mid-sentence; firing response.create immediately then gets
 * auto-cancelled by interrupt_response the instant the user keeps talking — "the AI
 * can't get a word out."
 *
 * Fix: wait VAD_RAPID_FIRE_GRACE_PERIOD_MS before firing. A speech_started within that
 * window cancels the wait; the next transcript (already accumulating in sentenceCache)
 * re-arms it. response.cancel is never sent here — see the speech_started branch below.
 */
export function createRealtimeMessageHandler(deps: RealtimeMessageHandlerDeps) {
  const {
    dataChannel,
    contextRequestGenerationRef,
    isWaitingForModelResponseRef,
    isUserCurrentlySpeakingRef,
    pendingAssistantTranscriptRef,
    lastCompletedAssistantMessageRef,
    baseInstructionsRef,
    noiseReductionTypeRef,
    transcriptRef,
    sentenceCacheRef,
    timeLeftRef,
    isCountdownCompleteRef,
    setIsUploading,
    setIsConnected,
    callDetails,
  } = deps;

  // Holds the pending "turn is really over" decision. Plain closure var (not a ref) —
  // this factory runs once per call session.
  let pendingResponseTimer: ReturnType<typeof setTimeout> | null = null;

  // True from the timer firing until commitPendingResponse settles — pendingResponseTimer
  // alone misses this window, since it's nulled the instant the timer fires.
  let isCommittingTurn = false;

  // OpenAI's own session id, captured from session.created, added to log context so
  // entries can be cross-referenced against OpenAI's own dashboards.
  let realtimeSessionId: string | undefined;

  // Retries up to MAX_PERSONALITY_CONTEXT_ATTEMPTS times (500ms apart); throws once exhausted.
  const MAX_PERSONALITY_CONTEXT_ATTEMPTS = 3;
  async function fetchPersonalityContextAsync(args: any) {
    let lastError: any = null;
    for (let attempt = 1; attempt <= MAX_PERSONALITY_CONTEXT_ATTEMPTS; attempt++) {
      try {
        const response = await api.fetchPersonalityContext({
          sentence: args.sentence,
          personality: Array.isArray(args.personality) ? args.personality?.join(",") : args.personality || "",
          practitionerType: args.practitionerType,
          secondsLeft: args.secondsLeft,
          hcpSentence: args.hcpSentence,
          sessionId: args.sessionId,
          objectionLevel: args.objectionLevel,
          topics: Array.isArray(args.topics) ? args.topics : [],
        });
        return response?.data || null;
      } catch (error) {
        lastError = error;
        if (attempt < MAX_PERSONALITY_CONTEXT_ATTEMPTS) {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      }
    }
    posthog.captureException(lastError, {
      properties: {
        callId: callDetails?.id,
        realtimeSessionId,
        name: "personality_context_async_error",
      },
    });
    throw lastError;
  }

  // Send response.create with baseInstructions + optional personality context injected.
  function triggerResponseWithContext(context: any) {
    if (!dataChannel.current || dataChannel.current.readyState !== "open") {
      posthog.captureException(new Error("dataChannel unavailable when triggering response"), {
        properties: { callId: callDetails?.id, realtimeSessionId, name: "trigger_response_dropped", readyState: dataChannel.current?.readyState ?? "null" },
      });
      setIsUploading(false);
      return;
    }

    let instructions = baseInstructionsRef.current;
    if (context) {
      instructions += "\n\n" + context;
    }

    try {
      getLoggerRef()?.info('Sending response.create to OpenAI', {
        sessionId: callDetails?.id,
        realtimeSessionId,
        personalityContext: context,
      });
      dataChannel.current.send(JSON.stringify({
        type: "response.create",
        response: { instructions, tool_choice: "none" },
      }));
      // Only clear once the send has actually happened — not before, and not if it
      // throws below. This is the one point where we know for certain this content
      // has been consumed; anything pushed after this belongs to the next turn.
      sentenceCacheRef.current = [];
    } catch (sendError) {
      posthog.captureException(sendError, {
        properties: { callId: callDetails?.id, realtimeSessionId, name: "dataChannel_trigger_error" },
      });
      setIsUploading(false);
    }
  }

  // Fetches personality context immediately, overlapping it with the grace-period wait.
  // Snapshots sentenceCache/hcpSentence first so later state changes can't corrupt this
  // turn's request. Tagged with contextRequestGenerationRef so it can be superseded.
  function beginFetchingPersonalityContext(): { generation: number; promise: Promise<ContextFetchResult> } {
    contextRequestGenerationRef.current++;
    const generation = contextRequestGenerationRef.current;

    // Snapshot mutable refs before the async boundary.
    const sentence = sentenceCacheRef.current.join(" ");
    const hcpSentence = lastCompletedAssistantMessageRef.current;
    const secondsLeft = timeLeftRef.current;

    const topics = (callDetails as { topics?: string[] }).topics;

    getLoggerRef()?.info('User speech turn: fetching personality context', {
      sessionId: callDetails?.id,
      realtimeSessionId,
      sentence,
      secondsLeft,
      practitionerType: callDetails?.practitionerType,
      objectionLevel: callDetails?.objectionLevel,
      personalityType: callDetails?.personalityType,
      topics,
    });

    const promise: Promise<ContextFetchResult> = fetchPersonalityContextAsync({
      sentence,
      hcpSentence,
      secondsLeft,
      practitionerType: callDetails?.practitionerType,
      sessionId: callDetails?.id,
      objectionLevel: callDetails?.objectionLevel,
      personality: callDetails?.personalityType,
      topics,
    }).then(
      (context) => {
        getLoggerRef()?.info('Personality context received', { sessionId: callDetails?.id, realtimeSessionId, hasContext: !!context });
        return { ok: true, context } as const;
      },
      (err) => {
        getLoggerRef()?.error("Personality context fetch failed", err as Error, { callId: callDetails?.id, sessionId: callDetails?.id, realtimeSessionId });
        return { ok: false } as const;
      }
    );

    return { generation, promise };
  }

  // Runs once the grace period elapses undisturbed: awaits the fetch and, if not
  // superseded, triggers the response. elapsedSinceTurnStartMs measures just the grace
  // period, since the fetch already ran concurrently with it.
  async function commitPendingResponse(fetch: { generation: number; promise: Promise<ContextFetchResult> }, turnStartedAt: number) {
    const result = await fetch.promise;
    const elapsedSinceTurnStartMs = Date.now() - turnStartedAt;

    // Superseded by a later fragment or a genuine interruption — discard.
    if (contextRequestGenerationRef.current !== fetch.generation) return;

    if (!result.ok) {
      getLoggerRef()?.warn('Grace period elapsed but personality-context fetch failed; discarding turn', {
        sessionId: callDetails?.id,
        realtimeSessionId,
        elapsedSinceTurnStartMs,
      });
      isWaitingForModelResponseRef.current = false;
      setIsUploading(false);
      sentenceCacheRef.current = [];
      ShowGenericErrorToast({ lead: MISSED_TURN_ERROR_LEAD });
      return;
    }

    getLoggerRef()?.info('Grace period elapsed, triggering AI response', {
      sessionId: callDetails?.id,
      realtimeSessionId,
      elapsedSinceTurnStartMs,
    });
    triggerResponseWithContext(result.context);
  }

  const handleMessage = async function handleRealtimeMessage(e: MessageEvent) {
    try {
      const data = JSON.parse(e.data);

      // Log event types only (not payloads) — delta events are skipped, see isHighFrequencyRealtimeEvent.
      if (!isHighFrequencyRealtimeEvent(data.type)) {
        getLoggerRef()?.info(`OpenAI event: ${data.type}`, { sessionId: callDetails?.id, realtimeSessionId });
      }

      // --- User speech started ---
      if (data.type === "input_audio_buffer.speech_started") {
        isUserCurrentlySpeakingRef.current = true;

        // Cancel the pending wait — nothing was sent to OpenAI, so nothing to cancel
        // server-side; the next completed transcript re-arms it.
        if (pendingResponseTimer) {
          clearTimeout(pendingResponseTimer);
          pendingResponseTimer = null;
          // Dedicated message so flickers are directly searchable, not inferred.
          getLoggerRef()?.info('VAD flicker detected: cancelled pending turn buffer', {
            sessionId: callDetails?.id,
            realtimeSessionId,
            bufferedFragments: sentenceCacheRef.current.length,
          });
        }

        if (isWaitingForModelResponseRef.current) {
          contextRequestGenerationRef.current++;
          // Deliberately not sending response.cancel: interrupt_response already
          // auto-cancels server-side before our message could land, and without a
          // response_id it could hit a later, unrelated response instead — a real bug.
          getLoggerRef()?.info('User interrupted a pending response (client-side state only, no response.cancel sent)', { sessionId: callDetails?.id, realtimeSessionId });
          isWaitingForModelResponseRef.current = false;
          setIsUploading(false);
        }
      }

      // --- User speech stopped ---
      else if (data.type === "input_audio_buffer.speech_stopped") {
        isUserCurrentlySpeakingRef.current = false;
      }

      // --- User speech transcription completed ---
      else if (data.type === "conversation.item.input_audio_transcription.completed") {
        if (!isCountdownCompleteRef.current) {
          setIsUploading(false);
          return;
        }
        const transcript = data.transcript;

        if (transcript?.trim()?.length > 0) {
          getLoggerRef()?.info('User speech transcribed', { sessionId: callDetails?.id, realtimeSessionId, transcript });
          transcriptRef.current.push({ role: "user", content: transcript });
          sentenceCacheRef.current.push(transcript);
        }
        // Don't bail on an empty transcript (noise VAD) — gate on sentenceCache below
        // instead, so a noise speech_started that cancelled a real utterance's timer can
        // still re-arm it rather than stranding that utterance forever.

        // Skip arming if the user's still talking — the next transcript picks up the
        // accumulated cache and arms a fresh one.
        if (isUserCurrentlySpeakingRef.current) {
          setIsUploading(false);
        } else if (sentenceCacheRef.current.length > 0) {
          // Don't fire immediately — semantic_vad can report the turn complete
          // mid-sentence; wait a grace period in case speech resumes.
          if (pendingResponseTimer) {
            clearTimeout(pendingResponseTimer);
          }
          // Marks this turn's start for elapsedSinceTurnStartMs — reset on every re-arm.
          const turnStartedAt = Date.now();

          // Start the fetch now so it overlaps the grace-period wait instead of
          // stacking after it.
          const fetch = beginFetchingPersonalityContext();
          pendingResponseTimer = setTimeout(() => {
            pendingResponseTimer = null;
            if (isUserCurrentlySpeakingRef.current) {
              return; // Defensive — shouldn't happen, speech_started clears this timer.
            }
            // Set before the await so a speech_started mid-fetch is treated as a real
            // interruption (via the isWaitingForModelResponseRef branch above).
            isWaitingForModelResponseRef.current = true;
            isCommittingTurn = true;
            commitPendingResponse(fetch, turnStartedAt).finally(() => {
              isCommittingTurn = false;
            });
          }, VAD_RAPID_FIRE_GRACE_PERIOD_MS);
        } else {
          // Nothing pending and no new content — just noise.
          isWaitingForModelResponseRef.current = false;
          setIsUploading(false);
        }
      }

      // --- Assistant audio transcript completed ---
      // Buffer only — response.done confirms status "completed" before we commit it.
      else if (data.type === "response.output_audio_transcript.done") {
        const transcript = data.transcript;
        if (transcript?.trim()?.length > 0) {
          pendingAssistantTranscriptRef.current = transcript;
        }
      }

      // --- Assistant response completed ---
      else if (data.type === "response.done") {
        isWaitingForModelResponseRef.current = false;
        if (data.response?.status === "completed") {
          if (pendingAssistantTranscriptRef.current) {
            getLoggerRef()?.info('AI response completed', { sessionId: callDetails?.id, realtimeSessionId, transcript: pendingAssistantTranscriptRef.current });
            transcriptRef.current.push({ role: "assistant", content: pendingAssistantTranscriptRef.current });
            lastCompletedAssistantMessageRef.current = pendingAssistantTranscriptRef.current;
          }
          pendingAssistantTranscriptRef.current = "";
          sentenceCacheRef.current = [];
        } else {
          // Commit the partial transcript so personality-context has record of it
          // (matches iOS). lastCompletedAssistantMessageRef stays unset — isInterrupted
          // below clears it so the incomplete turn isn't used as hcpSentence.
          if (isInterrupted(data) && pendingAssistantTranscriptRef.current) {
            transcriptRef.current.push({ role: "assistant", content: pendingAssistantTranscriptRef.current });
          }
          // Failed/incomplete: discard without committing.
          pendingAssistantTranscriptRef.current = "";
          setIsUploading(false);
        }
      }

      // Reset state on interruption
      if (isInterrupted(data)) {
        setIsUploading(false);
        // client_cancelled is unreachable here — we never send response.cancel (see
        // speech_started above). sentenceCache is not touched here — it's only ever
        // cleared right after a successful send (see triggerResponseWithContext), so by
        // the time this confirmation arrives it already correctly reflects either
        // nothing (this response's own content) or a newer, real barge-in utterance.
        // Clearing it here too could wipe that barge-in content on a late confirmation.
        if (data?.response?.status_details?.reason === "turn_detected" && !pendingResponseTimer && !isCommittingTurn) {
          contextRequestGenerationRef.current++;
          lastCompletedAssistantMessageRef.current = "";
        }
      }
      if (data.type === "session.created") {
        // Capture base instructions for re-injection on every response.create.
        const instructions = data.session?.instructions;
        if (instructions) {
          baseInstructionsRef.current = instructions;
        }
        realtimeSessionId = data.session?.id;
        // GA requires explicit session type to be set after session.created.
        dataChannel.current?.send(JSON.stringify({
          type: "session.update",
          session: {
            type: "realtime",
            audio: { input: { noise_reduction: { type: noiseReductionTypeRef.current } } },
          },
        }));
        getLoggerRef()?.warn('OpenAI Realtime session connected', { sessionId: callDetails?.id, realtimeSessionId });
        setIsConnected(true);
      }
      if (data.type === "conversation.item.added" && isCountdownCompleteRef.current) {
        setIsUploading(true);
      }
      if (data.type === "response.content_part.added") {
        isWaitingForModelResponseRef.current = false;
        setIsUploading(false);
      }
      if (data.type === "error") {
        const errorCode: string | undefined = data.error?.code;
        const cancelCodes = ["response_cancel_error", "cancellation_failed", "response_cancel_not_active"];
        if (cancelCodes.includes(errorCode ?? "")) {
          // Benign: response.cancel arrived after the response already completed. Ignore.
          getLoggerRef()?.info('OpenAI Realtime cancel race (ignored)', { sessionId: callDetails?.id, realtimeSessionId, errorCode });
        } else {
          getLoggerRef()?.error('OpenAI Realtime error event', undefined, {
            callId: callDetails?.id,
            realtimeSessionId,
            errorCode,
            errorMessage: data.error?.message,
          });
          posthog.captureException(new Error(`OpenAI Realtime error: ${errorCode}`), {
            properties: { callId: callDetails?.id, realtimeSessionId, errorCode, errorMessage: data.error?.message },
          });
          isWaitingForModelResponseRef.current = false;
          setIsUploading(false);
          ShowToast({ message: "Something went wrong with the call. If this keeps happening, try refreshing.", type: "warning" });
        }
      }
    } catch (error) {
      getLoggerRef()?.error("Data channel message processing error", error as Error, { callId: callDetails?.id, realtimeSessionId, source: 'openai-realtime-datachannel' });
      posthog.captureException(error, {
        properties: {
          callId: callDetails?.id,
          realtimeSessionId,
          name: "message_event_error",
        },
      });
      setIsUploading(false);
      ShowGenericErrorToast({ lead: MISSED_TURN_ERROR_LEAD });
    }
  };

  // stopSession can't otherwise reach this closure's timer — without this, a call ending
  // mid-grace-period still fires a stale response into an already-closed data channel.
  function cleanup() {
    if (pendingResponseTimer) {
      clearTimeout(pendingResponseTimer);
      pendingResponseTimer = null;
    }
    contextRequestGenerationRef.current++;
  }

  return { handleMessage, cleanup };
}
